import React, { useState, useEffect } from 'react';
import {
  Header,
  Icon,
  Table,
  Button,
  Loader,
  Message,
  Input,
  Form,
  Grid,
  Segment,
  Label,
  Dimmer,
  Checkbox,
  List
} from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import GlassTable from '../../components/Common/GlassTable';
import { planServices } from '../../services/plans/planServices';
import { PlanResponse, PlanFilters, CreatePlanRequest, CreatePlanServiceRequest } from '../../types/dtos';
import { DesignSystem, getDefaultButtonStyle, getButtonHoverEffects } from '../../styles/designSystem';
import BaseModal, { BaseModalAction } from '../../components/shared/BaseModal';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../context/AuthContext';

const PlansPage: React.FC = () => {
  const [plans, setPlans] = useState<PlanResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanResponse | null>(null);
  const [viewMode, setViewMode] = useState<'create' | 'edit' | 'view'>('create');
  const [formData, setFormData] = useState<Partial<CreatePlanRequest>>({
    name: '',
    description: '',
    basePrice: 0,
    dataAllowanceGb: 0,
    services: []
  });
  const [dataAllowanceGb, setDataAllowanceGb] = useState<number>(0);
  const [calculatedBasePrice, setCalculatedBasePrice] = useState(0);
  const [services, setServices] = useState<CreatePlanServiceRequest[]>([]);
  const [editingServiceIndex, setEditingServiceIndex] = useState<number | null>(null);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [newService, setNewService] = useState<CreatePlanServiceRequest>({
    name: '',
    description: '',
    monthlyValue: 0,
    enabled: true
  });
  const [filters, setFilters] = useState<PlanFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();
  
  // Verificar se o usuário tem permissão de Admin (pode editar/criar/deletar)
  const isAdmin = user?.roles && user.roles.includes('Admin');
  
  // Verificar se o usuário pode visualizar (Admin, Manager, Seller, TeamLeader)
  const canView = user?.roles && (user.roles.includes('Admin') || user.roles.includes('Manager') || user.roles.includes('Seller') || user.roles.includes('TeamLeader'));

  useEffect(() => {
    loadPlans();
  }, [filters]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Se há filtros ativos, usar filtros avançados
      if (Object.keys(filters).some(key => filters[key as keyof PlanFilters] !== undefined && filters[key as keyof PlanFilters] !== '')) {
        const plansData = await planServices.getPlansWithFilters(filters);
        setPlans(plansData);
      } else {
        // Usar busca normal
        const plansData = await planServices.getAllPlans();
        setPlans(plansData);
      }
    } catch (err) {
      setError('Erro ao carregar planos');
      console.error('Erro ao carregar planos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = () => {
    setSelectedPlan(null);
    setViewMode('create');
    setFormData({
      name: '',
      description: '',
      basePrice: 0,
      dataAllowanceGb: 0,
      services: []
    });
    setDataAllowanceGb(0);
    setServices([]);
    setCalculatedBasePrice(0);
    setNewService({
      name: '',
      description: '',
      monthlyValue: 0,
      enabled: true
    });
    setEditingServiceIndex(null);
    setShowServiceForm(false);
    setModalOpen(true);
  };

  const handleEditPlan = (plan: PlanResponse) => {
    setSelectedPlan(plan);
    setViewMode('edit');
    const planServices = plan.services.map(service => ({
      name: service.name,
      description: service.description,
      monthlyValue: service.monthlyValue,
      enabled: service.enabled
    }));
    
    setFormData({
      name: plan.name,
      description: plan.description,
      basePrice: 0, // Será calculado automaticamente
      dataAllowanceGb: plan.dataAllowanceGb,
      services: []
    });
    setDataAllowanceGb(plan.dataAllowanceGb);
    setServices(planServices);
    setNewService({
      name: '',
      description: '',
      monthlyValue: 0,
      enabled: true
    });
    setEditingServiceIndex(null);
    setShowServiceForm(false);
    setModalOpen(true);
  };

  const handleViewPlan = (plan: PlanResponse) => {
    setSelectedPlan(plan);
    setViewMode('view');
    setModalOpen(true);
  };

  const handleDeletePlan = async (planId: string) => {
    const plan = plans.find(p => p.id === planId);
    const planName = plan?.name || 'este plano';
    
    if (window.confirm(`Tem certeza que deseja excluir o plano "${planName}"?\n\nO plano será marcado como excluído (soft delete) e não aparecerá mais na lista, mas os dados serão mantidos no banco de dados.`)) {
      try {
        setLoading(true);
        await planServices.deletePlan(planId);
        showSuccess(`Plano "${planName}" excluído com sucesso`);
        await loadPlans();
      } catch (err: any) {
        const errorMessage = err?.response?.data?.message || err?.message || 'Erro desconhecido ao excluir plano';
        showError(`Erro ao excluir plano: ${errorMessage}`);
        console.error('Erro ao excluir plano:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSavePlan = async () => {
    try {
      // Validação: pelo menos um serviço deve ser adicionado
      if (services.length === 0) {
        showError('Adicione pelo menos um serviço ao plano');
        return;
      }

      // Validação: pelo menos um serviço deve estar ativo
      const activeServices = services.filter(service => service.enabled);
      if (activeServices.length === 0) {
        showError('Pelo menos um serviço deve estar ativo');
        return;
      }

      const planData: CreatePlanRequest = {
        name: formData.name!,
        description: formData.description!,
        basePrice: calculatedBasePrice, // Usar o preço calculado
        dataAllowanceGb: dataAllowanceGb, // Enviar GB diretamente
        services: services
      };

      if (viewMode === 'create') {
        await planServices.createPlan(planData);
        showSuccess('Plano criado com sucesso');
      } else if (viewMode === 'edit' && selectedPlan) {
        await planServices.updatePlan(selectedPlan.id, {
          name: planData.name,
          description: planData.description,
          basePrice: calculatedBasePrice, // Usar o preço calculado
          dataAllowanceGb: dataAllowanceGb // Enviar GB diretamente
        });
        showSuccess('Plano atualizado com sucesso');
      }

      setModalOpen(false);
      loadPlans();
    } catch (err) {
      showError('Erro ao salvar plano');
      console.error('Erro ao salvar plano:', err);
    }
  };

  const handleFilterChange = (field: keyof PlanFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  const addService = () => {
    setNewService({
      name: '',
      description: '',
      monthlyValue: 0,
      enabled: true
    });
    setEditingServiceIndex(null);
    setShowServiceForm(true);
  };

  const saveService = () => {
    if (!newService.name.trim()) {
      showError('Nome do serviço é obrigatório');
      return;
    }

    if (newService.monthlyValue < 0) {
      showError('Valor mensal não pode ser negativo');
      return;
    }

    if (editingServiceIndex !== null) {
      // Editando serviço existente
      setServices(prev => prev.map((service, i) => 
        i === editingServiceIndex ? { ...newService } : service
      ));
      showSuccess('Serviço atualizado com sucesso');
    } else {
      // Adicionando novo serviço
      setServices(prev => [...prev, { ...newService }]);
      showSuccess('Serviço adicionado com sucesso');
    }

    // Limpar formulário
    setNewService({
      name: '',
      description: '',
      monthlyValue: 0,
      enabled: true
    });
    setEditingServiceIndex(null);
    setShowServiceForm(false);
  };

  const editService = (index: number) => {
    const service = services[index];
    setNewService({ ...service });
    setEditingServiceIndex(index);
    setShowServiceForm(true);
  };

  const cancelEditService = () => {
    setNewService({
      name: '',
      description: '',
      monthlyValue: 0,
      enabled: true
    });
    setEditingServiceIndex(null);
    setShowServiceForm(false);
  };

  const removeService = (index: number) => {
    setServices(prev => prev.filter((_, i) => i !== index));
    showSuccess('Serviço removido com sucesso');
  };

  const updateNewService = (field: keyof CreatePlanServiceRequest, value: any) => {
    setNewService(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceFormKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveService();
    }
  };

  // Calcular preço base baseado nos serviços
  const calculateBasePrice = (servicesList: CreatePlanServiceRequest[]) => {
    return servicesList
      .filter(service => service.enabled)
      .reduce((total, service) => total + (service.monthlyValue || 0), 0);
  };

  // Atualizar preço base quando serviços mudarem
  useEffect(() => {
    const newBasePrice = calculateBasePrice(services);
    setCalculatedBasePrice(newBasePrice);
    setFormData(prev => ({ ...prev, basePrice: newBasePrice }));
  }, [services]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDataSize = (mb: number) => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(1)} GB`;
    }
    return `${mb} MB`;
  };

  const getModalTitle = () => {
    switch (viewMode) {
      case 'create': return 'Criar Novo Plano';
      case 'edit': return 'Editar Plano';
      case 'view': return 'Visualizar Plano';
      default: return 'Plano';
    }
  };

  const getModalIcon = () => {
    switch (viewMode) {
      case 'create': return 'plus';
      case 'edit': return 'edit';
      case 'view': return 'eye';
      default: return 'list';
    }
  };

  const modalActions: BaseModalAction[] = viewMode === 'view' 
    ? [
        {
          key: 'close',
          content: 'Fechar',
          onClick: () => setModalOpen(false),
          primary: false
        }
      ]
    : [
        {
          key: 'cancel',
          content: 'Cancelar',
          onClick: () => setModalOpen(false),
          primary: false
        },
        {
          key: 'save',
          content: viewMode === 'create' ? 'Criar' : 'Salvar',
          onClick: handleSavePlan,
          primary: true,
          loading: loading
        }
      ];

  return (
    <GlassPageWrapper title="Gerenciamento de Planos" icon="list alternate">

      {/* Filtros */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <Header as="h3" style={{ margin: 0, color: DesignSystem.colors.neutral.black }}>
            <Icon name="filter" />
            Filtros
          </Header>
          <Button
            icon={showFilters ? 'chevron up' : 'chevron down'}
            content={showFilters ? 'Ocultar' : 'Mostrar'}
            onClick={() => setShowFilters(!showFilters)}
            style={getDefaultButtonStyle()}
            {...getButtonHoverEffects()}
          />
        </div>

        {showFilters && (
          <Grid>
            <Grid.Row>
              <Grid.Column width={4}>
                <Form.Field>
                  <label>Nome do Plano</label>
                  <Input
                    placeholder="Buscar por nome..."
                    value={filters.name || ''}
                    onChange={(e) => handleFilterChange('name', e.target.value)}
                    icon="search"
                    iconPosition="left"
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={3}>
                <Form.Field>
                  <label>Preço Mínimo</label>
                  <Input
                    type="number"
                    placeholder="R$ 0,00"
                    value={filters.minPrice || ''}
                    onChange={(e) => handleFilterChange('minPrice', e.target.value ? Number(e.target.value) : undefined)}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={3}>
                <Form.Field>
                  <label>Preço Máximo</label>
                  <Input
                    type="number"
                    placeholder="R$ 0,00"
                    value={filters.maxPrice || ''}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value ? Number(e.target.value) : undefined)}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={3}>
                <Form.Field>
                  <label>Franquia Mínima (GB)</label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="0"
                    value={filters.minDataAllowanceGb || ''}
                    onChange={(e) => handleFilterChange('minDataAllowanceGb', e.target.value ? Number(e.target.value) : undefined)}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={3}>
                <Form.Field>
                  <label>Franquia Máxima (GB)</label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="0"
                    value={filters.maxDataAllowanceGb || ''}
                    onChange={(e) => handleFilterChange('maxDataAllowanceGb', e.target.value ? Number(e.target.value) : undefined)}
                  />
                </Form.Field>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={4}>
                <Form.Field>
                  <Checkbox
                    label="Apenas planos com serviços"
                    checked={filters.hasServices || false}
                    onChange={(e, { checked }) => handleFilterChange('hasServices', checked || undefined)}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={4}>
                <Form.Field>
                  <Checkbox
                    label="Apenas planos ativos"
                    checked={filters.isActive || false}
                    onChange={(e, { checked }) => handleFilterChange('isActive', checked || undefined)}
                  />
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={8} textAlign="right">
                <Button
                  content="Limpar Filtros"
                  icon="refresh"
                  onClick={clearFilters}
                  style={getDefaultButtonStyle()}
                  {...getButtonHoverEffects()}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        )}
      </div>

      {/* Ações */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {isAdmin && (
            <Button
              primary
              icon="plus"
              content="Novo Plano"
              onClick={handleCreatePlan}
              style={getDefaultButtonStyle()}
              {...getButtonHoverEffects()}
            />
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ color: DesignSystem.colors.neutral.gray.dark }}>
            {plans.length} plano(s) encontrado(s)
          </span>
        </div>
      </div>

      {/* Tabela */}
      <div style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <Dimmer active>
            <Loader size="large">Carregando planos...</Loader>
          </Dimmer>
        ) : error ? (
          <Message negative>
            <Message.Header>Erro</Message.Header>
            <p>{error}</p>
          </Message>
        ) : plans.length === 0 ? (
          <Message info>
            <Message.Header>Nenhum plano encontrado</Message.Header>
            <p>Não há planos cadastrados ou que correspondam aos filtros aplicados.</p>
          </Message>
        ) : (
          <GlassTable celled striped style={{ margin: 0 }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Nome</Table.HeaderCell>
                <Table.HeaderCell>Descrição</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Preço Base</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Franquia (GB)</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Serviços</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Preço Total</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Status</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Ações</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {plans.map((plan) => (
                <Table.Row key={plan.id}>
                  <Table.Cell>
                    <Header as="h4" style={{ margin: 0 }}>
                      {plan.name}
                    </Header>
                  </Table.Cell>
                  <Table.Cell>
                    <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {plan.description}
                    </div>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <strong>{formatCurrency(plan.basePrice)}</strong>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <div>
                      <strong>{plan.dataAllowanceGb.toFixed(1)} GB</strong>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        ({formatDataSize(plan.dataAllowanceMb)})
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Label color={plan.services.length > 0 ? 'blue' : 'grey'}>
                      {plan.services.length} serviço(s)
                    </Label>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <strong style={{ color: DesignSystem.colors.status.success }}>
                      {formatCurrency(plan.totalMonthlyPrice)}
                    </strong>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Label color={plan.isActive ? 'green' : 'red'}>
                      {plan.isActive ? 'Ativo' : 'Inativo'}
                    </Label>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                      <Button
                        icon="eye"
                        size="mini"
                        onClick={() => handleViewPlan(plan)}
                        style={getDefaultButtonStyle()}
                        {...getButtonHoverEffects()}
                      />
                      <Button
                        icon="edit"
                        size="mini"
                        onClick={() => handleEditPlan(plan)}
                        disabled={!isAdmin}
                        style={getDefaultButtonStyle()}
                        {...getButtonHoverEffects()}
                        title={!isAdmin ? 'Apenas administradores podem editar planos' : 'Editar plano'}
                      />
                      <Button
                        icon="trash"
                        size="mini"
                        color="red"
                        onClick={() => handleDeletePlan(plan.id)}
                        disabled={!isAdmin}
                        style={getDefaultButtonStyle()}
                        {...getButtonHoverEffects()}
                        title={!isAdmin ? 'Apenas administradores podem excluir planos' : 'Excluir plano'}
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </GlassTable>
        )}
      </div>

      {/* Modal de Formulário */}
      <BaseModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={getModalTitle()}
        icon={getModalIcon()}
        size="large"
        actions={modalActions}
      >
        {viewMode === 'view' ? (
          // Modo Visualização
          <div style={{ padding: '1rem' }}>
            {selectedPlan && (
              <Grid>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Header as="h3">Informações Básicas</Header>
                    <p><strong>Nome:</strong> {selectedPlan.name}</p>
                    <p><strong>Descrição:</strong> {selectedPlan.description}</p>
                    <p><strong>Preço Base:</strong> {formatCurrency(selectedPlan.basePrice)}</p>
                    <p><strong>Franquia:</strong> {selectedPlan.dataAllowanceGb.toFixed(1)} GB ({formatDataSize(selectedPlan.dataAllowanceMb)})</p>
                    <p><strong>Preço Total:</strong> {formatCurrency(selectedPlan.totalMonthlyPrice)}</p>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Header as="h3">Serviços Incluídos</Header>
                    {selectedPlan.services.length > 0 ? (
                      <List>
                        {selectedPlan.services.map((service, index) => (
                          <List.Item key={index}>
                            <List.Content>
                              <List.Header>{service.name}</List.Header>
                              <List.Description>
                                {service.description} - {formatCurrency(service.monthlyValue)}/mês
                                {service.enabled ? ' (Ativo)' : ' (Inativo)'}
                              </List.Description>
                            </List.Content>
                          </List.Item>
                        ))}
                      </List>
                    ) : (
                      <p>Nenhum serviço incluído</p>
                    )}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            )}
          </div>
        ) : (
          // Modo Criação/Edição
          <div style={{ padding: '1rem' }}>
            <Form>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Form.Field required>
                      <label>Nome do Plano</label>
                      <Input
                        placeholder="Ex: Plano Empresarial Premium"
                        value={formData.name || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        disabled={false}
                      />
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Form.Field>
                      <label>Preço Base (R$) - Calculado Automaticamente</label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={calculatedBasePrice}
                        disabled={true}
                        style={{ 
                          backgroundColor: '#f8f9fa', 
                          color: '#495057',
                          fontWeight: 'bold'
                        }}
                      />
                      <div style={{ 
                        fontSize: '0.8rem', 
                        color: '#6c757d', 
                        marginTop: '0.25rem' 
                      }}>
                        Soma dos valores mensais dos serviços ativos
                      </div>
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Form.Field required>
                      <label>Franquia de Dados (GB)</label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="1.0"
                        value={dataAllowanceGb || ''}
                        onChange={(e) => {
                          const gbValue = Number(e.target.value);
                          setDataAllowanceGb(gbValue);
                          setFormData(prev => ({ ...prev, dataAllowanceGb: gbValue }));
                        }}
                        disabled={false}
                      />
                      <div style={{ 
                        fontSize: '0.8rem', 
                        color: '#6c757d', 
                        marginTop: '0.25rem' 
                      }}>
                        Digite a franquia em GB (ex: 1.5 para 1.5 GB)
                      </div>
                    </Form.Field>
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Form.Field>
                      <label>Franquia em MB (Calculado)</label>
                      <Input
                        value={Math.round(dataAllowanceGb * 1024)}
                        disabled
                        placeholder="Calculado automaticamente"
                        style={{ 
                          backgroundColor: '#f8f9fa', 
                          color: '#495057',
                          fontWeight: 'bold'
                        }}
                      />
                      <div style={{ 
                        fontSize: '0.8rem', 
                        color: '#6c757d', 
                        marginTop: '0.25rem' 
                      }}>
                        Valor enviado para o backend
                      </div>
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Form.Field required>
                      <label>Descrição</label>
                      <Form.TextArea
                        placeholder="Descreva os benefícios e características do plano..."
                        value={formData.description || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        disabled={false}
                        rows={3}
                      />
                    </Form.Field>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <div>
                        <Header as="h3" style={{ margin: 0 }}>Serviços Incluídos</Header>
                        <div style={{ 
                          fontSize: '0.9rem', 
                          color: DesignSystem.colors.status.success,
                          fontWeight: 'bold',
                          marginTop: '0.25rem'
                        }}>
                          Total: {formatCurrency(calculatedBasePrice)}
                        </div>
                      </div>
                      {viewMode === 'create' && isAdmin && (
                        <Button
                          icon="plus"
                          content="Adicionar Serviço"
                          onClick={addService}
                          size="small"
                          style={getDefaultButtonStyle()}
                          {...getButtonHoverEffects()}
                        />
                      )}
                    </div>
                    
                    {/* Formulário para adicionar/editar serviço */}
                    {showServiceForm && (
                      <Segment 
                        style={{ 
                          marginBottom: '1rem',
                          border: `2px solid ${DesignSystem.colors.primary.start}`,
                          backgroundColor: '#f8f9ff'
                        }}
                        onKeyDown={handleServiceFormKeyPress}
                      >
                        <Header as="h4" style={{ marginBottom: '1rem', color: DesignSystem.colors.primary.start }}>
                          <Icon name="edit" />
                          {editingServiceIndex !== null ? 'Editar Serviço' : 'Adicionar Novo Serviço'}
                          <div style={{ 
                            fontSize: '0.8rem', 
                            fontWeight: 'normal', 
                            color: '#6c757d',
                            marginTop: '0.25rem'
                          }}>
                            💡 Dica: Pressione Enter em qualquer campo para salvar
                          </div>
                        </Header>
                        <Grid>
                          <Grid.Row>
                            <Grid.Column width={6}>
                              <Form.Field required>
                                <label>Nome do Serviço</label>
                                <Input
                                  placeholder="Ex: TIM Nuvem"
                                  value={newService.name}
                                  onChange={(e) => updateNewService('name', e.target.value)}
                                />
                              </Form.Field>
                            </Grid.Column>
                            <Grid.Column width={4}>
                              <Form.Field required>
                                <label>Valor Mensal (R$)</label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="0.00"
                                  value={newService.monthlyValue}
                                  onChange={(e) => updateNewService('monthlyValue', Number(e.target.value))}
                                />
                                <div style={{ 
                                  fontSize: '0.8rem', 
                                  color: '#6c757d', 
                                  marginTop: '0.25rem' 
                                }}>
                                  Use 0,00 para serviços gratuitos
                                </div>
                              </Form.Field>
                            </Grid.Column>
                            <Grid.Column width={4}>
                              <Form.Field>
                                <label>Status</label>
                                <Form.Select
                                  options={[
                                    { key: true, text: 'Ativo', value: true },
                                    { key: false, text: 'Inativo', value: false }
                                  ]}
                                  value={newService.enabled}
                                  onChange={(e, { value }) => updateNewService('enabled', value)}
                                />
                              </Form.Field>
                            </Grid.Column>
                            <Grid.Column width={2}>
                              <Form.Field>
                                <label>&nbsp;</label>
                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                  <Button
                                    icon="save"
                                    color="green"
                                    size="mini"
                                    onClick={saveService}
                                    style={{ flex: 1 }}
                                  />
                                  <Button
                                    icon="cancel"
                                    color="grey"
                                    size="mini"
                                    onClick={cancelEditService}
                                    style={{ flex: 1 }}
                                  />
                                </div>
                              </Form.Field>
                            </Grid.Column>
                          </Grid.Row>
                          <Grid.Row>
                            <Grid.Column width={16}>
                              <Form.Field>
                                <label>Descrição do Serviço</label>
                                <Input
                                  placeholder="Descreva o serviço..."
                                  value={newService.description}
                                  onChange={(e) => updateNewService('description', e.target.value)}
                                />
                              </Form.Field>
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                      </Segment>
                    )}

                    {/* Lista de serviços salvos */}
                    {services.length === 0 ? (
      <Message info>
                        <p>Nenhum serviço adicionado. Clique em "Adicionar Serviço" para incluir serviços no plano.</p>
      </Message>
                    ) : (
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {services.map((service, index) => (
                          <Segment key={index} style={{ 
                            marginBottom: '0.5rem',
                            border: service.enabled ? `2px solid ${DesignSystem.colors.status.success}` : '2px solid #e9ecef',
                            backgroundColor: service.enabled ? '#f8fff8' : '#f8f9fa'
                          }}>
                            <Grid>
                              <Grid.Row>
                                <Grid.Column width={6}>
                                  <div>
                                    <strong>{service.name}</strong>
                                    {service.enabled && (
                                      <span style={{ 
                                        color: DesignSystem.colors.status.success, 
                                        fontSize: '0.8rem',
                                        marginLeft: '0.5rem'
                                      }}>
                                        ✓ {service.monthlyValue > 0 ? 'Contribui para o preço base' : 'Incluído gratuitamente'}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                                    {service.description || 'Sem descrição'}
                                  </div>
                                </Grid.Column>
                                <Grid.Column width={4}>
                                  <div>
                                    <strong style={{ 
                                      color: service.monthlyValue > 0 ? DesignSystem.colors.status.success : DesignSystem.colors.neutral.gray.dark 
                                    }}>
                                      {service.monthlyValue > 0 ? formatCurrency(service.monthlyValue) : 'Gratuito'}
                                    </strong>
                                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                      {service.monthlyValue > 0 ? 'por mês' : 'sem custo'}
                                    </div>
                                  </div>
                                </Grid.Column>
                                <Grid.Column width={4}>
                                  <div>
                                    <Label color={service.enabled ? 'green' : 'red'}>
                                      {service.enabled ? 'Ativo' : 'Inativo'}
                                    </Label>
                                  </div>
                                </Grid.Column>
                                <Grid.Column width={2}>
                                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                                    {isAdmin && (
                                      <>
                                        <Button
                                          icon="edit"
                                          size="mini"
                                          onClick={() => editService(index)}
                                          style={getDefaultButtonStyle()}
                                          {...getButtonHoverEffects()}
                                        />
                                        <Button
                                          icon="trash"
                                          color="red"
                                          size="mini"
                                          onClick={() => removeService(index)}
                                        />
                                      </>
                                    )}
                                  </div>
                                </Grid.Column>
                              </Grid.Row>
                            </Grid>
                          </Segment>
                        ))}
                      </div>
                    )}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          </div>
        )}
      </BaseModal>
    </GlassPageWrapper>
  );
};

export default PlansPage;
